// useInternalLinkRoute.ts

import { useCategorySearch } from "#imports";

export function useInternalLinkRoute() {
  const { advancedSearch: categorySearch } = useCategorySearch();
  const routeCache = ref<Record<string, string>>({});

  const buildAndCacheRouteFromCategory = (category: any) => {
    if (!category) {
      return null;
    }

    const categoryId = category.id;

    if (!categoryId) {
      return null;
    }

    if (routeCache.value[categoryId]) {
      return routeCache.value[categoryId];
    }

    if (category?.seoUrls?.length) {
      const canonicalUrl = category.seoUrls.find((url: any) => url.isCanonical === true);
      const seoUrl = canonicalUrl || category.seoUrls[0];
      const route = `/${seoUrl.seoPathInfo}`;
      routeCache.value[categoryId] = route;
      return route;
    }

    const fallbackRoute = `/navigation/${categoryId}`;
    routeCache.value[categoryId] = fallbackRoute;
    return fallbackRoute;
  };


  const prefetchInternalLinkRoutes = async (navigationItems: any[]) => {
    // Collect unique internalLink IDs that we don't have cached yet
    const idsToFetch = Array.from(
      new Set(
        navigationItems
          .filter(
            (item) =>
              item &&
              item.linkType === "category" &&
              item.internalLink &&
              !routeCache.value[item.internalLink]
          )
          .map((item) => item.internalLink as string)
      )
    );

    if (!idsToFetch.length) {
      return;
    }

    try {
      const data = await categorySearch({
        withCmsAssociations: false,
        query: {
          filter: [
            {
              type: "equalsAny",
              field: "id",
              value: idsToFetch.join("|"),
            },
          ],
          associations: {
            seoUrls: {},
          },
        },
      });

      // Cache routes for all returned categories
      if (Array.isArray(data)) {
        for (const category of data) {
          buildAndCacheRouteFromCategory(category);
        }
      }

      // For any IDs still not in cache, add a fallback
      for (const id of idsToFetch) {
        if (!routeCache.value[id]) {
          const fallbackRoute = `/navigation/${id}`;
          routeCache.value[id] = fallbackRoute;
        }
      }
    } catch (error) {
      console.error("Error prefetching internal link categories:", error);

      // On error, still populate fallbacks so we don't break navigation
      for (const id of idsToFetch) {
        if (!routeCache.value[id]) {
          const fallbackRoute = `/navigation/${id}`;
          routeCache.value[id] = fallbackRoute;
        }
      }
    }
  };

  return {
    prefetchInternalLinkRoutes,
    routeCache,
  };
} 